import React, {
  useCallback,
  useContext,
  useEffect,
  useReducer,
  useRef,
  useState,
  useMemo,
} from 'react';
import api from '../api';
import Container from 'react-bootstrap/Container';
import { Form, Button, Nav, Modal, Dropdown } from 'react-bootstrap';
import { Store } from '../Store';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import MessageBox from '../components/general/MessageBox';
import { getError, checkAndReplaceInactiveFonts } from '../utils';
import { deepCopy } from '../utils/deepObjects';
import {
  DEFAULT_DESIGN_STATE,
  applyDesignUpdates,
  pushSnapshot,
} from '../utils/designState';
import {
  findFirstActualKeycap,
  hasKeycapEntries,
  keycapClickUpdates,
} from '../utils/keycapSelection';
import {
  dismissCustomLayoutNotice,
  initializeStorage,
  isCustomLayoutNoticeDismissed,
  safeLocalStorageSet,
  storedDesignOrDefault,
  migratedDefaultDesign,
  loadedDesignSource,
} from '../utils/customizerStorage';
import {
  blankKeycapData,
  designForLayout,
  designOnLayout,
  searchedLayouts,
  designWithResetEditors,
  layoutDisplayName,
  layoutGroups,
  restrictedLayoutIds,
  layoutWithId,
  layoutOfDesign,
  productOfLayout,
  profileStock,
  isProfileSoldOut,
  isLayoutSoldOut,
  layoutProfiles,
  profileOptionLabel,
} from '../utils/customizerLayout';
import {
  designNameToSave,
  customizerCartItem,
  prepareKeycapDataForUpload,
} from '../utils/designUpload';
import {
  designReplacedFontsReport,
  replacedFontsNumbers,
} from '../utils/fontReplacementReport';
import {
  customizerQuantityFromInput,
  customizerQuantityLimit,
} from '../utils/formInputs';
import { mergeStoredKeycapData } from '../utils/storedDesigns';
import {
  uploadedIconsNeedSignIn,
  verifyUserIconAssetAccess,
} from '../utils/userIconAccess';
import {
  useEscapeUnselect,
  useUndoRedoShortcuts,
} from '../utils/customizerShortcuts';
import { useUserIconAssets } from '../utils/useUserIconAssets';
import { useUserIconPreviews } from '../utils/useUserIconPreviews';
import { toast } from 'react-toastify';
import { predefinedTagColors } from '../components/customizer/shared/customizerScreen/tagColors';
import { v4 as uuidv4 } from 'uuid';
import SaveButton from '../components/customizer/shared/customizerScreen/SaveButton';
import DropdownMenu from '../components/customizer/shared/customizerScreen/DropdownMenu';
import KeycapStroke from '../components/customizer/shared/customizerScreen/KeycapStroke';
import KeycapBaseLayer from '../components/customizer/shared/customizerScreen/KeycapBaseLayer';
import StaticKeycapShadows from '../components/customizer/shared/customizerScreen/StaticKeycapShadows';
import KeycapOverlay from '../components/customizer/shared/customizerScreen/KeycapOverlay';
import KeycapImageRendering from '../components/customizer/shared/customizerScreen/KeycapImageRendering';
import KeycapColorEditor from '../components/customizer/shared/customizerScreen/KeycapColorEditor';
import TextEditor from '../components/customizer/shared/customizerScreen/TextEditor';
import IconEditor from '../components/customizer/shared/customizerScreen/IconEditor';
// import ImageEditor from '../components/customizer/ImageEditor';
import { saveToIndexedDB } from '../indexedDB';
import LoadingLogo from '../components/general/LoadingLogo';
import { formatEuro } from '../utils';
import { saveAs } from 'file-saver';
import { useTranslation } from 'react-i18next';
import { fetchFont } from '../utils/fontHelpers';
import { Alert } from 'react-bootstrap';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import {
  normalizeAlphaPaintsForSvgExport,
  convertTextNodesToPaths,
  cloneAndPrepSvg,
  svgToString,
  inlinePngUserIconDataUrlsForSvgExport,
} from '../utils/exportHelpers';
import {
  migrateDesignIfNeeded,
  getMigratedDesignAssetFromIndexedDB,
} from '../utils/customizerCompat';
import { getFromIndexedDB } from '../indexedDB';
import {
  collectUserIconAssetIds,
  designUsesUserIconAsset,
  removeUserIconAssetFromDesign,
} from '../utils/userIconAssets';
import {
  calculateLayoutWidth,
  calculateLayoutHeight,
} from '../utils/layoutGeometry';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        layouts: action.payload.layoutsData,
        products: action.payload.productsData,
        loading: false,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'CREATE_REQUEST':
      return { ...state, loadingCreate: true };
    case 'CREATE_SUCCESS':
      return {
        ...state,
        loadingCreate: false,
      };
    case 'CREATE_FAIL':
      return { ...state, loadingCreate: false };

    default:
      return state;
  }
};

export default function CustomizerScreen() {
  const [{ layouts, products, loading, error }, dispatch] = useReducer(
    reducer,
    {
      loading: true,
      error: '',
      layouts: [],
      products: [],
    }
  );

  const { t } = useTranslation();

  const [showAlert, setShowAlert] = useState(false); // state to track if the alert (telling the user to turn off the browser’s tracking protection) should be displayed

  const textEditorRef = useRef(); // a reference to the TextEditor component
  const iconEditorRef = useRef(); // a reference to the IconEditor component
  const navRef = useRef(null); // a reference to the Customizer tabs navigation
  const tourRef = useRef(null); // a reference to the Customizer tour

  function waitForElement(selector, options) {
    // Set default options if not provided:
    options = options || {};
    var visible = options.visible !== undefined ? options.visible : true;
    var timeout = options.timeout !== undefined ? options.timeout : 4000; // max wait time (ms)
    var interval = options.interval !== undefined ? options.interval : 50; // how often to check (ms)

    return new Promise(function (resolve, reject) {
      var start = performance.now(); // record start time
      var timer = setInterval(function () {
        var el = document.querySelector(selector); // try to find the element
        var isVisible = el && el.offsetParent !== null; // check if it's visible (basic check)

        if (el && (!visible || isVisible)) {
          // element is found and either visibility is not required or it's visible
          clearInterval(timer); // stop checking
          resolve(el); // continue with the element
        } else if (performance.now() - start > timeout) {
          // timeout reached
          clearInterval(timer); // stop checking
          reject(new Error('Timeout waiting for ' + selector)); // return error
        }
      }, interval); // repeat every `interval` ms
    });
  } // This helper function takes an id of an element as a prop and will wait until the element will appear in the DOM. This is useful when I need to interact with an element that has not yet been rendered.

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenCustomizerTour'); // check in the users local storage if he has seen the tour

    // create the driver.js tour of the Customizer:
    const tour = driver({
      showProgress: true,
      showButtons: true,
      steps: [
        {
          // No element: center popover
          popover: {
            title: t('customizer-screen-tour-step-1.1'),
            description: `
                <div style="max-width:320px; line-height:1.4;">
                  ${t('customizer-screen-tour-step-1.2')}
                  <ul style="margin:6px 0 10px 18px; padding:0;">
                    <li>${t('customizer-screen-tour-step-1.3')}</li>
                    <li>${t('customizer-screen-tour-step-1.4')}</li>
                  </ul>
                  ${t('customizer-screen-tour-step-1.5')}
                </div>
              `,
            side: 'center',
            align: 'center',
          },
        },
        {
          element: '#customizer-tabs',
          popover: {
            title: t('customizer-screen-tour-step-2.1'),
            description: t('customizer-screen-tour-step-2.2'),
            side: 'top',
            align: 'start',
            onNextClick: () => {
              updateDesign({ 'settings.selectedEditor': 'layout' });
              tour.moveNext();
            },
          },
        },
        {
          element: '#layout-tab',
          popover: {
            title: t('customizer-screen-tour-step-3.1'),
            description: t('customizer-screen-tour-step-3.2'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#layout-select',
          popover: {
            title: t('customizer-screen-tour-step-4.1'),
            description: t('customizer-screen-tour-step-4.2'),
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
              updateDesign({ 'settings.selectedEditor': 'profile' });
              tour.moveNext();
            },
          },
        },
        {
          element: '#profile-tab',
          popover: {
            title: t('customizer-screen-tour-step-5.1'),
            description: t('customizer-screen-tour-step-5.2'),
            side: 'top',
            align: 'start',
            onPrevClick: () => {
              updateDesign({ 'settings.selectedEditor': 'layout' });

              waitForElement('#layout-select')
                .then(() => {
                  tour.movePrevious();
                })
                .catch((err) => {
                  tour.movePrevious(); // Fallback: still move back so the tour doesn’t stall forever
                });
            },
          },
        },
        {
          element: '#profile-select',
          popover: {
            title: t('customizer-screen-tour-step-6.1'),
            description: t('customizer-screen-tour-step-6.2'),
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
              updateDesign({ 'settings.selectedEditor': 'colors' });
              tour.moveNext();
            },
          },
        },
        {
          element: '#colors-tab',
          popover: {
            title: t('customizer-screen-tour-step-7.1'),
            description: t('customizer-screen-tour-step-7.2'),
            side: 'top',
            align: 'start',
            onPrevClick: () => {
              updateDesign({ 'settings.selectedEditor': 'profile' });

              waitForElement('#profile-select')
                .then(() => {
                  tour.movePrevious();
                })
                .catch((err) => {
                  tour.movePrevious(); // Fallback: still move back so the tour doesn’t stall forever
                });
            },
          },
        },
        {
          element: '#keycap-group',
          popover: {
            title: t('customizer-screen-tour-step-8.1'),
            description: t('customizer-screen-tour-step-8.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#customDesignedKeycapSet',
          popover: {
            title: t('customizer-screen-tour-step-9.1'),
            description: t('customizer-screen-tour-step-9.2'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#color-list',
          popover: {
            title: t('customizer-screen-tour-step-10.1'),
            description: t('customizer-screen-tour-step-10.2'),
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'colors', // make sure the user is still in the colors editor
                'settings.selectedKeycaps': [clicked], // select the keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              tour.moveNext();
            },
          },
        },
        {
          element: '#color-picker',
          popover: {
            title: t('customizer-screen-tour-step-11.1'),
            description: t('customizer-screen-tour-step-11.2'),
            side: 'top',
            align: 'start',
          },
        },
        {
          element: '#color-randomizer',
          popover: {
            title: t('customizer-screen-tour-step-12.1'),
            description: t('customizer-screen-tour-step-12.2'),
            side: 'bottom',
            align: 'start',
            onPrevClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'colors', // make sure the user is still in the colors editor
                'settings.selectedKeycaps': [clicked], // select the keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              tour.movePrevious();
            },
            onNextClick: () => {
              // switch to the text tab
              // unselect the selected keycap
              updateDesign({
                'settings.selectedEditor': 'text',
                'settings.selectedKeycaps': [],
              });

              tour.moveNext();
            },
          },
        },
        {
          element: '#text-tab',
          popover: {
            title: t('customizer-screen-tour-step-13.1'),
            description: t('customizer-screen-tour-step-13.2'),
            side: 'top',
            align: 'start',
            onPrevClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'colors', // switch to the colors tab
                'settings.selectedKeycaps': [clicked], // select the keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              waitForElement('#color-randomizer')
                .then(() => tour.movePrevious())
                .catch(() => tour.movePrevious()); // Fallback: still move back so the tour doesn’t stall forever
            },
          },
        },
        {
          element: '#predefined-legends',
          popover: {
            title: t('customizer-screen-tour-step-14.1'),
            description: t('customizer-screen-tour-step-14.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#customDesignedKeycapSet',
          popover: {
            title: t('customizer-screen-tour-step-15.1'),
            description: t('customizer-screen-tour-step-15.2'),
            side: 'top',
            align: 'start',
            onNextClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'text', // make sure the user is still in the text editor
                'settings.selectedKeycaps': [clicked], // set the selected keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              tour.moveNext();
            },
          },
        },
        {
          element: '#text-entries',
          popover: {
            title: t('customizer-screen-tour-step-16.1'),
            description: t('customizer-screen-tour-step-16.2'),
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {}; // as the other seven steps: before the layout has loaded this selects a position that doesn't exist, which the next click replaces (KNOWN_BUGS.md, the tour before the layout)
              const clicked = { rowIndex, keycapIndex };

              updateDesign({
                'settings.selectedEditor': 'text', // make sure the user is still in the text editor
                'settings.selectedKeycaps': [clicked], // set the selected keycap
                'settings.isoEnterSelected':
                  !!designRef.current?.keycapData?.[rowIndex]?.[keycapIndex]
                    ?.isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              // if the selected keycap has no text entries, add one and select it:
              if (
                !hasKeycapEntries(
                  designRef.current.keycapData,
                  rowIndex,
                  keycapIndex,
                  'texts'
                )
              ) {
                textEditorRef.current?.handleKeycapChange(
                  'texts',
                  {},
                  null,
                  true // addNew
                ); // call the handleKeycapChange function from inside the TextEditor component
              } else {
                // otherwise select the first text entry:
                textEditorRef.current?.updateTextEditor('1'); // call the updateTextEditor function from inside the TextEditor component
              }

              tour.moveNext();
            },
          },
        },
        {
          element: '#text-entry-settings',
          popover: {
            title: t('customizer-screen-tour-step-17.1'),
            description: t('customizer-screen-tour-step-17.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-settings-tag',
          popover: {
            title: t('customizer-screen-tour-step-18.1'),
            description: t('customizer-screen-tour-step-18.2'),
            side: 'bottom',
            align: 'end',
          },
        },
        {
          element: '#text-entry-grid',
          popover: {
            title: t('customizer-screen-tour-step-19.1'),
            description: t('customizer-screen-tour-step-19.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-cell',
          popover: {
            title: t('customizer-screen-tour-step-20.1'),
            description: t('customizer-screen-tour-step-20.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-alignment',
          popover: {
            title: t('customizer-screen-tour-step-21.1'),
            description: t('customizer-screen-tour-step-21.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-manual-positioning',
          popover: {
            title: t('customizer-screen-tour-step-22.1'),
            description: t('customizer-screen-tour-step-22.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-visual-guidelines',
          popover: {
            title: t('customizer-screen-tour-step-23.1'),
            description: t('customizer-screen-tour-step-23.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-font',
          popover: {
            title: t('customizer-screen-tour-step-24.1'),
            description: t('customizer-screen-tour-step-24.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#text-entry-style',
          popover: {
            title: t('customizer-screen-tour-step-25.1'),
            description: t('customizer-screen-tour-step-25.2'),
            side: 'top',
            align: 'start',
            onNextClick: () => {
              // switch to the icons tab
              // deselect the selected keycap
              updateDesign({
                'settings.selectedEditor': 'icons',
                'settings.selectedKeycaps': [],
              });

              tour.moveNext();
            },
          },
        },
        {
          element: '#icons-tab',
          popover: {
            title: t('customizer-screen-tour-step-26.1'),
            description: t('customizer-screen-tour-step-26.2'),
            side: 'top',
            align: 'start',
            onPrevClick: () => {
              // switch to the text tab:
              updateDesign({ 'settings.selectedEditor': 'text' });

              waitForElement('#text-entry-style')
                .then(() => {
                  // get the position of the first non-spacer keycap:
                  const { rowIndex, keycapIndex } =
                    findFirstActualKeycap(designRef.current.keycapData) || {};
                  const clicked = { rowIndex, keycapIndex };
                  const keycap =
                    designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
                  const isIsoEnter = !!keycap?.isIsoEnter;

                  updateDesign({
                    'settings.selectedEditor': 'text',
                    'settings.selectedKeycaps': [clicked], // select the keycap
                    'settings.isoEnterSelected': isIsoEnter,
                    'keycapColorEditor.selectedColorEntry': null,
                    'textEditor.selectedColorEntry': null,
                    'textEditor.selectedTextIndex': null,
                    'textEditor.selectedTag': null, // reset tag selection
                    'iconEditor.selectedColorEntry': null,
                    'iconEditor.selectedIconIndex': null,
                    'iconEditor.selectedTag': null, // reset tag selection
                  });

                  // if the selected keycap has no text entries, add one and select it:
                  if (
                    !hasKeycapEntries(
                      designRef.current.keycapData,
                      rowIndex,
                      keycapIndex,
                      'texts'
                    )
                  ) {
                    textEditorRef.current?.handleKeycapChange(
                      'texts',
                      {},
                      null,
                      true // addNew
                    ); // call the handleKeycapChange function from inside the TextEditor component
                  } else {
                    // otherwise select the first text entry:
                    textEditorRef.current?.updateTextEditor('1'); // call the updateTextEditor function from inside the TextEditor component
                  }

                  tour.movePrevious();
                })
                .catch(() => {
                  tour.movePrevious(); // Fallback: still move back so the tour doesn’t stall forever
                });
            },
          },
        },
        {
          element: '#icon-editor',
          popover: {
            title: t('customizer-screen-tour-step-27.1'),
            description: t('customizer-screen-tour-step-27.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#customDesignedKeycapSet',
          popover: {
            title: t('customizer-screen-tour-step-28.1'),
            description: t('customizer-screen-tour-step-28.2'),
            side: 'top',
            align: 'start',
            onNextClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'icons', // make sure the user is still in the icon editor
                'settings.selectedKeycaps': [clicked], // select the keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              tour.moveNext();
            },
          },
        },
        {
          element: '#icon-entries',
          popover: {
            title: t('customizer-screen-tour-step-29.1'),
            description: t('customizer-screen-tour-step-29.2'),
            side: 'bottom',
            align: 'start',
            onNextClick: () => {
              // get the position of the first non-spacer keycap:
              const { rowIndex, keycapIndex } =
                findFirstActualKeycap(designRef.current.keycapData) || {};
              const clicked = { rowIndex, keycapIndex };
              const keycap =
                designRef.current?.keycapData?.[rowIndex]?.[keycapIndex];
              const isIsoEnter = !!keycap?.isIsoEnter;

              updateDesign({
                'settings.selectedEditor': 'icons', // make sure the user is still in the icon editor
                'settings.selectedKeycaps': [clicked], // select the keycap
                'settings.isoEnterSelected': isIsoEnter,
                'keycapColorEditor.selectedColorEntry': null,
                'textEditor.selectedColorEntry': null,
                'textEditor.selectedTextIndex': null,
                'textEditor.selectedTag': null, // reset tag selection
                'iconEditor.selectedColorEntry': null,
                'iconEditor.selectedIconIndex': null,
                'iconEditor.selectedTag': null, // reset tag selection
              });

              // if the selected keycap has no icon entries, add one and select it:
              if (
                !hasKeycapEntries(
                  designRef.current.keycapData,
                  rowIndex,
                  keycapIndex,
                  'icons'
                )
              ) {
                iconEditorRef.current?.handleKeycapChange(
                  'icons',
                  {},
                  null,
                  true // addNew
                ); // call the handleKeycapChange function from inside the IconEditor component
              } else {
                // otherwise select the first icon entry:
                iconEditorRef.current?.handleSelectIconEntry('1'); // call the handleSelectIconEntry function from inside the IconEditor component
              }

              tour.moveNext();
            },
          },
        },
        {
          element: '#icon-settings-tag',
          popover: {
            title: t('customizer-screen-tour-step-30.1'),
            description: t('customizer-screen-tour-step-30.2'),
            side: 'bottom',
            align: 'end',
          },
        },
        {
          element: '#icon-entry-position',
          popover: {
            title: t('customizer-screen-tour-step-31.1'),
            description: t('customizer-screen-tour-step-31.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#icon-entry-style',
          popover: {
            title: t('customizer-screen-tour-step-32.1'),
            description: t('customizer-screen-tour-step-32.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#icon-library',
          popover: {
            title: t('customizer-screen-tour-step-33.1'),
            description: t('customizer-screen-tour-step-33.2'),
            side: 'bottom',
            align: 'start',
          },
        },
        {
          element: '#save-button',
          popover: {
            title: t('customizer-screen-tour-step-34.1'),
            description: t('customizer-screen-tour-step-34.2'),
            side: 'bottom',
            align: 'end',
          },
        },
        {
          element: '#customizer-dropdown-menu',
          popover: {
            title: t('customizer-screen-tour-step-35.1'),
            description: t('customizer-screen-tour-step-35.2'),
            side: 'bottom',
            align: 'end',
          },
        },
        {
          // No element: center popover
          popover: {
            title: t('customizer-screen-tour-step-36.1'),
            description: t('customizer-screen-tour-step-36.2'),
            side: 'center',
            align: 'center',
          },
        },
      ],
      onDestroyStarted: () => {
        if (
          !tour.hasNextStep() ||
          window.confirm(t('customizer-screen-exit-tour')) // ask the user if he really wants to exit the tour before having finished it
        ) {
          tour.destroy();
          localStorage.setItem('hasSeenCustomizerTour', 'true'); // set inside the users local storage that he has seen the tour
        }
      }, // onDestroyStarted is called when the user tries to exit the tour
    });

    tourRef.current = tour; // store the tour inside the tourRef, so I can let the user take the tour later again by clicking on a button

    // Only run once when element is mounted and the user visits the Customizer for the first time:
    if (!hasSeenTour && navRef.current) {
      tour.drive(); // Start tour on first load
    }

    return () => {
      if (tour && tour.isActive && tour.isActive()) {
        tour.destroy(); // cleanup: stop tour if user navigates away or component unmounts (return () => { ... } in useEffect runs when: The component unmounts. Or the dependency (navRef.current) changes)
      }
    };
  }, [navRef.current]); // this useEffect runs once on the first render, creates the driver.js tour, stores it inside the tourRef and starts the tour if the user has not seen it before

  const PREFIX = 'design_'; // the prefix to set and get design data to and from the users localStorage

  const [design, setDesign] = useState(() =>
    storedDesignOrDefault(`${PREFIX}state`)
  ); // the stored design, migrated, or the default: the screen's first render shows it until the load effect below has read the storage again

  const [designStorageInitialized, setDesignStorageInitialized] =
    useState(false); // prevents auto-saving before localStorage/import/layout initialization has finished

  // refs to store the timers
  const throttleTimeoutRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const saveNow = useCallback((state) => {
    safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(state));
  }, []); // helper function to keep the design_state in the users localStorage updated to the design state

  const throttleSaveDesignState = useCallback(
    (state, delay) => {
      if (throttleTimeoutRef.current) return; // if a timer is already running, do nothing. This ensures we only schedule ONE save per throttle window, so repeated calls within the delay period are ignored.

      throttleTimeoutRef.current = setTimeout(() => {
        throttleTimeoutRef.current = null; // after the timer finishes and we save to localStorage, reset the ref back to null so the next call can schedule a new timer. Without this, we'd never allow future saves.
        saveNow(state); // Save the design state to localStorage after the specified delay (If delay = 500ms, the state is written to localStorage at most once every 500ms during continuous updates.)
      }, delay);
    },
    [saveNow]
  ); // throttling function for localStorage writes (Throttling means intermediate states are skipped. If the user makes rapid changes to the design state, the state is only being updated to the localStorage after the specified 'delay'. But after the delay, all changes that have been made up to this point, will be saved to the localStorage at once)

  const debounceSaveDesignState = useCallback(
    (state, delay) => {
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current); // Clear any existing timeout to restart the debounce delay

      debounceTimeoutRef.current = setTimeout(() => {
        saveNow(state); // Save the final design state to localStorage after the delay (If delay = 2000ms, the state will only be saved 2 seconds after the user stops making changes.)
      }, delay);
    },
    [saveNow]
  ); // debouncing ensures the design state is saved after a period of inactivity. This complements the throttling mechanism and ensures no changes are missed, even if the user makes a series of rapid updates and then stops interacting.

  useEffect(() => {
    if (!designStorageInitialized) {
      return; // do not save the temporary initial default design before localStorage/import initialization has finished
    }

    throttleSaveDesignState(design, 500); // Save state during rapid updates (throttled)
    debounceSaveDesignState(design, 2000); // Ensure final save after inactivity (debounced)
  }, [
    design,
    designStorageInitialized,
    throttleSaveDesignState,
    debounceSaveDesignState,
  ]); // this useEffect hook syncs the design state to the design_state in the users localStorage

  useEffect(() => {
    return () => {
      if (throttleTimeoutRef.current) clearTimeout(throttleTimeoutRef.current);
      if (debounceTimeoutRef.current) clearTimeout(debounceTimeoutRef.current);
    };
  }, []); // Cleanup all timeouts on component unmount

  const designRef = useRef(design); // a ref to the design state

  useEffect(() => {
    designRef.current = design; // Keep the ref updated with the latest design state (so that handleBeforeUnload uses the latest value of design, before the component unmounts)
  }, [design]);

  const designStorageInitializedRef = useRef(false); // keeps the latest initialization status available inside stable callbacks/event handlers

  useEffect(() => {
    designStorageInitializedRef.current = designStorageInitialized; // keep the ref in sync with the state value whenever initialization status changes
  }, [designStorageInitialized]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      if (throttleTimeoutRef.current) {
        clearTimeout(throttleTimeoutRef.current); // ensures that any pending throttled writes are canceled immediately
        throttleTimeoutRef.current = null; // reset so it can be used again
      }

      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current); // ensures that any pending debounce writes are canceled immediately
        debounceTimeoutRef.current = null; // reset so it can be used again
      }

      if (!designStorageInitializedRef.current) {
        return; // do not save the temporary initial design if the page unloads before initialization has finished
      }

      safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(designRef.current)); // forces a final save of the design state to localStorage (Use the latest design state from the ref)
    };

    window.addEventListener('beforeunload', handleBeforeUnload); // 'beforeunload' event fires when the browser is about to unload the document
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload); // ensures that the beforeunload event listener is removed when the component unmounts
    };
  }, []); // ensures that the most recent state of the design is saved to localStorage when the user navigates away from the page, closes the browser tab, or refreshes the page

  const resetDesignStorage = () => {
    setSelectedLegend(null); // Remove the predefined legends document

    updateDesign((prevDesign) =>
      designWithResetEditors(prevDesign, layout.layoutName, defaultKeycapData)
    ); // the layout's blank keycaps and name, the tags and the editors at their defaults
  }; // Reset design state and design_state localStorage

  const getLayoutDisplayName = useCallback(
    (layout) =>
      layoutDisplayName(layout, products, design.profileEditor.profile, t),
    [products, design.profileEditor.profile, t]
  ); // helper function to produce display name for layouts/products in the Dropdown of the search bar
  const { state, dispatch: ctxDispatch } = useContext(Store); // useContext gives me access to the state of the context (in this case the global Store) and lets me rename dispatch to ctxDisptach, to distinguish it from the dispatch of the current component
  const {
    cart: { cartItems },
    userInfo,
  } = state;
  const navigate = useNavigate();
  const location = useLocation();
  const [triggerKeycapShadowRerender, setTriggerKeycapShadowRerender] =
    useState(true);
  let totalVerticalSpacing = 0; // initialization of the totalVerticalSpacing variable. This variable is being used to accumulate the vertical spacing that comes with every row of keycaps and is needed to render the rows of keycaps at the correct height
  const [layoutWidth, setLayoutWidth] = useState(0);
  const [layoutHeight, setLayoutHeight] = useState(0);
  const [relevantGroups, setRelevantGroups] = useState([]); // contains all keycap groups the current selected layout has (for example the 60% keyboard has no numbad and therefor not keycaps with the group 'numbad')
  const [isSaveSuccessful, setIsSaveSuccessful] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [selectedProduct, setSelectedProduct] = useState([]);
  const renderCount = useRef(0); // the renderCount ref is initialized to true. This ref will persist for the lifetime of the component without causing re-renders when its value changes
  const [fonts, setFonts] = useState({}); // store loaded fontkit font objects
  const fontPromises = useRef({}); // in-flight fetches
  const [defaultKeycapData, setDefaultKeycapData] = useState([]); // the default keycapData without any texts, icons, etc.
  const [selectedLegend, setSelectedLegend] = useState(null); // Entire legend set document
  const [layout, setLayout] = useState(null);
  const [showCustomLayoutNotice, setShowCustomLayoutNotice] = useState(false); // shows an alert whenever the user selects a (custom) layout that is based on a restricted product, telling the user to save designs regularly
  const [hasAssignedKeycapIDs, setHasAssignedKeycapIDs] = useState(false); // true, whether the selected layout contains at least one key with a keycapId
  const [hasAssignedColorGroups, setHasAssignedColorGroups] = useState(false);
  const containerRef = useRef(null); // a ref to the layout search bar and dropdown, to check whenever the user closes the search bar by clicking outside
  const [layoutSearchTerm, setLayoutSearchTerm] = useState(''); // What the user sees in the search bar (might be typed, or the selected layout name)
  const [showLayoutList, setShowLayoutList] = useState(false); // Whether the dropdown is open
  const [showReplacementsModal, setShowReplacementsModal] = useState(false);
  const [replacedFontsSummary, setReplacedFontsSummary] = useState(null); // one design's summary once the load replaced inactive fonts, as checkAndReplaceInactiveFonts hands it over (null while nothing was replaced; an empty array until Nov 2026, which is truthy and has no keycaps: the modal read it bare since PR 163 and the customizer crashed once its product had loaded)
  const replacedFonts = useMemo(
    () =>
      replacedFontsSummary ? replacedFontsNumbers(replacedFontsSummary) : null,
    [replacedFontsSummary]
  ); // the fonts replaced and the texts deleted, as the downloadable report counts them (utils/fontReplacementReport.js), once per load for the modal's header and body; the expressions between the modal's tags run on every render of the screen, shown or not
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [designName, setDesignName] = useState('');
  const [hoveredKeycap, setHoveredKeycap] = useState(null); // state to track which keycap the user hovers over
  const currentLayoutId = design?.layoutEditor?.id || null; // get the id of the selected layout
  const customLayoutIds = useMemo(
    () => restrictedLayoutIds(products),
    [products]
  ); // gather the layout IDs from all restricted products

  const loadFont = useCallback(
    (cacheKey, apiUrl) =>
      fetchFont(cacheKey, apiUrl, fonts, fontPromises.current, setFonts),
    [fonts]
  ); // loads and caches a font once (fetchFont in utils/fontHelpers.js, one copy in four screens until chain 10, Oct 2026): the loaded fonts from the state, the fetches in flight from the ref

  // Design import:
  const { designId } = useParams();

  // Undo & Redo:
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  useEffect(() => {
    const fetchProductsAndLayouts = async () => {
      try {
        const response = await api.get('/api/products/customizer');

        const { products: productsData, layouts: layoutsData } = response.data;

        return { productsData, layoutsData };
      } catch (error) {
        console.error('Error fetching keycap and product data:', error.message);
        throw new Error(t('customizer-screen-keycap-and-product-fetch-fail'));
      }
    };

    const initializeDesignState = () => {
      const { value: designState, wasInitialized } = initializeStorage(
        `${PREFIX}state`,
        deepCopy(DEFAULT_DESIGN_STATE)
      ); // initialize the design_state in the users localStorage (if it doesn't already exist) or get the localStorage (if it already exists)

      setDesign(designState); // set the design state to the design_state data from the users localStorage, or to the DEFAULT_DESIGN_STATE if the data didn't exist in the users localStorage
      return { designState, wasInitialized };
    };

    const fetchLegend = async (designState) => {
      try {
        const legendId = designState.textEditor.predefinedLegends.id;

        const { data } = await api.get(`/api/legends/${legendId}`);
        setSelectedLegend(data);
      } catch (err) {
        console.error('Error fetching legend data:', err.message);
        toast.error(t('customizer-screen-legend-fetch-fail'));
      }
    }; // initializing a function to fetch predefined legends

    const sanitizeSingleDesignFonts = async (design) => {
      const { sanitizedDesignsData, replacedFontsSummary } =
        await checkAndReplaceInactiveFonts({
          designsData: design,
        });

      const finalDesign = sanitizedDesignsData || design;

      if (replacedFontsSummary) {
        setReplacedFontsSummary(replacedFontsSummary);
        toast.warning(t('customizer-screen-font-replacement-warning'));
        setShowReplacementsModal(true);
      }

      return finalDesign;
    }; // this function goes through every text entry of all keycaps, checks if inactive fonts are used in the design and replaces them with fallbacks

    const handleDesignImportFromCart = async (uniqueCartId, layoutsData) => {
      let designState;
      let wasInitialized = false;

      // Fetch the data from IndexedDB using the uniqueCartId
      const asset = await getMigratedDesignAssetFromIndexedDB(uniqueCartId); // loads the cart design from IndexedDB and migrates older saved design data if needed

      if (!asset || !asset.design) {
        throw new Error(
          `Design not found in IndexedDB for uniqueCartId: ${uniqueCartId}`
        );
      }

      const importedDesign = asset.design; // Access the design field from IndexedDB

      const selectedLayoutData = layoutOfDesign(layoutsData, importedDesign); // the layout the imported design sits on, undefined when no product references it any more

      if (!selectedLayoutData) {
        // If the layout is not found (no product references the layout of the imported design), reset to defaults.
        // Because it may be possible that an admin sets the isActive field of a Product to false and a previously available Product (Layout) is no longer active and the design state needs to be set to default values

        // Reset localStorage and update local design state variable to default:
        designState = migratedDefaultDesign(); // the default design at the current customizerVersion
        wasInitialized = true;

        toast.warning(t('customizer-screen-design-import-error'));

        return {
          selectedLayoutData: layoutsData[0],
          designState,
          wasInitialized,
        };
      } else {
        // If the layout for the imported design has been found, proceed with importing the design as usual:
        const sanitizedDesign = await sanitizeSingleDesignFonts(importedDesign); // sanitize for inactive fonts
        const preparedDesign = migrateDesignIfNeeded(sanitizedDesign); // ensure the sanitized design uses the latest customizerVersion

        const { uniqueCartId, pngDataUrl } = asset; // Access the uniqueCartId and pngDataUrl field from IndexedDB

        await saveToIndexedDB({
          uniqueCartId,
          design: preparedDesign,
          pngDataUrl,
        }); // save the sanitized design back to IndexedDB

        const finalDesign = {
          ...preparedDesign,
          name: selectedLayoutData.layoutName,
        }; // reset the name of the imported design to the default (the layoutName of the selected layout). Otherwise when the user makes changes to the design and adds the design to the cart, the design will be saved under the old name of the imported design.

        safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(finalDesign));

        setDesign(finalDesign); // Update the design state with imported design
        await fetchLegend(finalDesign); // fetch the predefined legends document based on the the selected language of the imported design

        return {
          selectedLayoutData,
          designState: finalDesign,
          wasInitialized,
        };
      }
    };

    const handleDesignImportFromDatabase = async (designId, layoutsData) => {
      let designState;
      let wasInitialized = false;

      const { data } = await api.get(`/api/designs/${designId}`); // fetching the design, user uploaded image and file extension of the uploaded image

      const { design: importedDesign } = data; // Extract the design from the response

      const selectedLayoutData = layoutOfDesign(layoutsData, importedDesign); // the layout the imported design sits on, undefined when no product references it any more

      if (!selectedLayoutData) {
        // If the layout is not found (no product references the layout of the imported design), reset to defaults.
        // Because it may be possible that an admin sets the isActive field of a Product to false and a previously available Product (Layout) is no longer active and the design state needs to be set to default values

        // Reset localStorage and update local design state variable to default:
        designState = migratedDefaultDesign(); // the default design at the current customizerVersion
        wasInitialized = true;

        toast.warning(t('customizer-screen-design-import-error'));

        return {
          selectedLayoutData: layoutsData[0],
          designState,
          wasInitialized,
        };
      } else {
        // If the layout for the imported design has been found, proceed with importing the design as usual:
        const importedKeycapData = mergeStoredKeycapData(
          selectedLayoutData.layout,
          importedDesign.keycapData
        ); // each stored keycap merged over the layout's keycap and the keycap defaults, with the layout's spacers put back (a design that has been saved to the data base only stores the most necessary values that differ from the layouts and defaultKeycapValues)

        const mergedDesign = {
          ...importedDesign,
          name: selectedLayoutData.layoutName, // reset the name of the imported design to the default (the layoutName of the selected layout). Otherwise when the user makes changes to the design and adds the design to the cart, the design will be saved under the old name of the imported design.
          keycapData: importedKeycapData,
        };

        const sanitizedDesign = await sanitizeSingleDesignFonts(mergedDesign); // sanitize for inactive fonts

        const preparedDesign = migrateDesignIfNeeded(sanitizedDesign); // migrate the design state to the latest customizerVerios

        safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(preparedDesign));
        setDesign(preparedDesign); // Update the design state with imported design

        await fetchLegend(preparedDesign); // fetch the predefined legends document based on the the selected language of the imported design

        return {
          selectedLayoutData,
          designState: preparedDesign,
          wasInitialized,
        };
      }
    };

    const handleDefaultDesign = async (
      designState,
      storedLayout,
      layoutsData
    ) => {
      let selectedLayoutData;

      if (storedLayout) {
        // if design_state has already been set in the users localStorage, when the user entered this screen, and its layout is still listed:
        selectedLayoutData = storedLayout;

        await fetchLegend(designState); // fetch the predefined legends document based on the the selected language of the users localStorage

        const sanitizedDesign = await sanitizeSingleDesignFonts(designState); // Sanitize fonts (only if user had an existing design)
        const preparedDesign = migrateDesignIfNeeded(sanitizedDesign); // ensure the sanitized design still uses the latest customizerVersion

        safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(preparedDesign));
        setDesign(preparedDesign);
      } else {
        // if no design_state data has been found in the users localStorage, set defaults:
        selectedLayoutData = layoutsData[0];
      }

      return selectedLayoutData;
    };

    const setupLayout = (selectedLayoutData) => {
      setRelevantGroups(layoutGroups(selectedLayoutData.layout)); // all keycap groups the selected layout has (for example the 60% keyboard has no numbad and therefor not keycaps with the group 'numbad')

      setLayoutWidth(calculateLayoutWidth(selectedLayoutData.layout));
      setLayoutHeight(calculateLayoutHeight(selectedLayoutData.layout));
    };

    const setupDefaultKeycapData = (selectedLayoutData) => {
      const defaultKeycapData = blankKeycapData(selectedLayoutData.layout); // the layout's keycaps with the defaultKeycapValues, white and without entries

      setDefaultKeycapData(defaultKeycapData);
      return defaultKeycapData;
    };

    const fetchData = async () => {
      dispatch({ type: 'FETCH_REQUEST' });

      try {
        // 1. Fetch keycaps, products and layouts data:
        const { productsData, layoutsData } = await fetchProductsAndLayouts();

        let designState;
        let wasInitialized;
        let selectedLayoutData;
        let profile; // the keycap profile the sold-out suffix of the search term is judged by

        const source = loadedDesignSource(designId); // the browser storage, a cart item's asset in IndexedDB or a design document, by the address

        if (source === 'cart') {
          // 2. If the user wants to import a design, fetch the design data, get the selectedLayoutData, add spacers to the keycapData (to match the structure of the selected layout), set the design state and fetch the legends document:
          // the designId is a uuid generated id, the design is coming from an item in the cart
          ({ selectedLayoutData, designState, wasInitialized } =
            await handleDesignImportFromCart(designId, layoutsData));
        } else if (source === 'database') {
          // the designId is not a uuid generated id, the design is imported from the MongoDB database
          ({ selectedLayoutData, designState, wasInitialized } =
            await handleDesignImportFromDatabase(designId, layoutsData));
        } else {
          // 3. If the user does not import a design, set the design state to the data found in the users localStorage, or set default state if localStorage has not yet been set:
          const result = initializeDesignState();
          designState = result.designState;
          wasInitialized = result.wasInitialized;

          let storedLayout; // the layout the stored design sits on: none at a first visit, and none once the reset below has replaced the design

          if (!wasInitialized) {
            // If the design state has not been initialized it means that the user already had a design state in his localStorage
            // In this case we need to check if the layout of the users localStorage design state is still listed (a product the user can see references it)
            // This is needed, because it may be possible that an admin sets the isActive field of a Product to false and a previously available Product is no longer active and the design state in the users localStorage needs to be set to default values
            storedLayout = layoutOfDesign(layoutsData, designState); // the imports look their layout up the same way; this check read the products until chain 8, which lists the same set, as the route sends exactly the products' layouts and no route deletes a layout

            if (!storedLayout) {
              // If no product references the localStorage layout, reset to defaults:
              designState = migratedDefaultDesign(); // the default design at the current customizerVersion
              wasInitialized = true;

              toast.warning(t('customizer-screen-layout-no-longer-available'));
            }
          }

          // 4. get the selectedLayoutData and fetch the legends document (if the user already had the localStorage initialized):
          selectedLayoutData = await handleDefaultDesign(
            designState,
            storedLayout,
            layoutsData
          );
        }

        setLayout(selectedLayoutData);

        // 5. Set the selected product based on the selected layout:
        const selectedProduct = productOfLayout(
          productsData,
          selectedLayoutData._id
        );
        setSelectedProduct(selectedProduct);

        // 6. Set the relevantGroups state and calculate the layoutWidth and layoutHeight:
        setupLayout(selectedLayoutData);

        // 7. create a default keycapData array for the selected layout (without any texts, icons, ...):
        const defaultKeycapData =
          await setupDefaultKeycapData(selectedLayoutData);

        // 8. If the user does not import a design and localStorage had to be initialized, update the design state accordingly:
        if (wasInitialized) {
          const updatedDesignState = designOnLayout(
            designState,
            selectedLayoutData,
            defaultKeycapData
          ); // the design put on the default layout: its name and blank keycaps, the first profile in stock, and whether it is tied to a restricted product (custom layout)

          profile = updatedDesignState.profileEditor.profile; // if the design has been initialized, use the default keycap profile to determine if the selected/default layout is sold out

          setDesign(updatedDesignState);
          safeLocalStorageSet(
            `${PREFIX}state`,
            JSON.stringify(updatedDesignState)
          );
        } else {
          profile = designState.profileEditor.profile; // if the design has not been initialized, use the keycap profile from the users design to determine if the selected layout is sold out
        }

        // 9. Set the search term in the layoutEditor
        setLayoutSearchTerm(
          layoutDisplayName(selectedLayoutData, productsData, profile, t)
        );

        // 10. Trigger a rerender of the keycap shadows:
        setTriggerKeycapShadowRerender((prev) => !prev);

        setDesignStorageInitialized(true); // allow future design changes to be saved to localStorage now that the correct initial/imported design has been loaded

        dispatch({
          type: 'FETCH_SUCCESS',
          payload: { layoutsData, productsData },
        });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: err.message });
        console.error(err);
      }
    };

    fetchData();

    if (designId) {
      // Remove designId from the URL
      const { pathname, search } = location;
      const newPath = pathname.replace(`/${designId}`, ''); // Remove designId from the path
      navigate(`${newPath}${search}`, { replace: true }); // Allows navigation to a new URL without reloading the page
    }
  }, [userInfo]); // this useEffect hook fetches the layouts data from the data base, sets states and triggers a re-render of the keycap shadows, when first loading the screen

  useEffect(() => {
    renderCount.current += 1;
    if (renderCount.current <= 1 || !layouts || layouts.length === 0) {
      // Skip the effect on the first render, or if layouts have not been set yet (this is needed because the useEffect from above calls setDesign, which may change the value of design.layoutEditor.id and therefor triggers this useEffect hook before the layouts have been set)
      return;
    }

    // 1. Get the selected layout and set the layout and layoutSearchTerm states:
    const layoutData = layoutWithId(layouts, design.layoutEditor.id);
    setLayout(layoutData);
    setLayoutSearchTerm(getLayoutDisplayName(layoutData));

    // 2. Set the selectedProduct, based on the new layout id:
    const selectedProduct = productOfLayout(products, layoutData._id);
    setSelectedProduct(selectedProduct);

    // 3. Set the relevantGroups state for the newly selected layout:
    setRelevantGroups(layoutGroups(layoutData.layout));

    // 4. Update the default keycapData array for the selected layout:
    const defaultKeycapData = blankKeycapData(layoutData.layout); // the layout's keycaps with the defaultKeycapValues, white and without entries
    setDefaultKeycapData(defaultKeycapData);

    // 5. Reset the totalVerticalSpacing value:
    totalVerticalSpacing = 0;

    // 6. Update the layoutWidth and layoutHeight for the SVG:
    setLayoutWidth(calculateLayoutWidth(layoutData.layout));
    setLayoutHeight(calculateLayoutHeight(layoutData.layout));

    // 7. Trigger a rerender of the keycap shadows:
    setTriggerKeycapShadowRerender((prev) => !prev);

    // 8. Fetch the predefined legends document, if the id is valid:
    const fetchLegend = async () => {
      try {
        const legendId = design.textEditor.predefinedLegends.id;

        const { data } = await api.get(`/api/legends/${legendId}`);
        setSelectedLegend(data);
      } catch (err) {
        console.error('Error fetching legend:', err.message);
        toast.error(t('customizer-screen-legend-fetch-fail'));
      }
    };

    if (design.textEditor.predefinedLegends?.id) {
      // only if the id is not null (which also means that the layout change was due to a undo or redo action, because if the user would have manually selected a new layout, the id would have been set to null), the legends document will be fetched
      fetchLegend();
    }
  }, [design.layoutEditor.id]); // This useEffect ensures that when the selected layout changes due to undo or redo actions, all the states that are not part of the design state will be updated too (also triggers a re-render of the keycap shadows)

  useEffect(() => {
    if (!layouts || layouts.length === 0) return; // skip until layouts are loaded

    const layoutData = layoutWithId(layouts, design.layoutEditor.id); // find the currently selected layout

    if (!layoutData || !Array.isArray(layoutData.layout)) {
      setHasAssignedKeycapIDs(false);
      setHasAssignedColorGroups(false);
      return;
    }

    const anyAssignedKeycapId = layoutData.layout.some(
      (row) =>
        Array.isArray(row) &&
        row.some((keycap) => !keycap.isSpacer && !!keycap.keycapId)
    ); // check if at least one keycap has a keycapId assigned

    const anyAssignedColorGroup = layoutData.layout.some(
      (row) =>
        Array.isArray(row) &&
        row.some(
          (keycap) =>
            !keycap.isSpacer &&
            keycap.colorGroup !== undefined &&
            keycap.colorGroup !== null
        )
    ); // check if at least one keycap has a colorGroup assigned (non-null, non-undefined, not 0)

    setHasAssignedKeycapIDs(anyAssignedKeycapId);
    setHasAssignedColorGroups(anyAssignedColorGroup);
  }, [design.layoutEditor.id, layouts]); // runs when selected layout changes; checks for any keycapId and colorGroup in layoutData.layout

  useEffect(() => {
    if (!currentLayoutId) {
      setShowCustomLayoutNotice(false); // don't show the custom layout notice, if there is no selected layout
      return;
    }

    const isRestricted = customLayoutIds.has(String(currentLayoutId)); // check if the selected layout is coming from a restricted product
    if (!isRestricted) {
      setShowCustomLayoutNotice(false); // don't show the custom layout notice, if the selected layout is not referenced in a restricted product
      return;
    }

    setShowCustomLayoutNotice(!isCustomLayoutNoticeDismissed(currentLayoutId)); // show the notice, if it has not been dismissed for the selected layout before
  }, [currentLayoutId, customLayoutIds]); // this useEffect displayes an alert in the UI, if the user has selected a custom layout, telling him to regularly save the design progress

  const dismissRestrictedNotice = () => {
    if (currentLayoutId) {
      dismissCustomLayoutNotice(currentLayoutId); // remember the dismissal for the selected layout in the users localStorage
    }

    setShowCustomLayoutNotice(false); // hide the alert
  }; // this function is called, whenever the user closes the alert of a custom layout (restricted product)

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setLayoutSearchTerm(getLayoutDisplayName(layout)); // Reset the layoutSearchTerm
        setShowLayoutList(false); // close the dropdown menu
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [layout, getLayoutDisplayName]); // Close layoutEditor dropdown if user clicks outside

  const pushToUndoStack = (currentDesign) => {
    const designCopy = deepCopy(currentDesign); // create a deep copy of the current design state
    setUndoStack((prevUndoStack) => pushSnapshot(prevUndoStack, designCopy)); // Add current design state to undo stack, within its size limit
    setRedoStack([]); // Clear redo stack on new action
  };

  const updateDesign = (updates) => {
    if (
      typeof updates !== 'function' &&
      (typeof updates !== 'object' || updates === null)
    ) {
      throw new Error(
        'Invalid argument: updates must be a non-null object or a function.'
      );
    }

    try {
      pushToUndoStack(design); // Push current state to undoStack

      setDesign((prevDesign) => applyDesignUpdates(prevDesign, updates)); // the design with the updates applied: the function's result, or the values written at their dotted paths

      setRedoStack([]); // Clear the redo stack on any new change
    } catch (error) {
      console.error('Error updating design:', error);
    }
  }; // function to handle updates to the design state (Also handles undo and redo stacks)

  const undoChange = useCallback(() => {
    if (undoStack.length > 0) {
      const newUndoStack = [...undoStack]; // Create a copy of undoStack
      const previousDesign = newUndoStack.pop(); // Remove the last item (undo operation)
      setUndoStack(newUndoStack);

      setRedoStack((prevRedoStack) => [...prevRedoStack, deepCopy(design)]); // Add current design to redoStack
      setDesign(previousDesign); // Set the design to the previous design
    }
  }, [undoStack, design]); // this function handles the undoChange

  const redoChange = useCallback(() => {
    if (redoStack.length > 0) {
      const newRedoStack = [...redoStack]; // Create a copy of redoStack
      const nextDesign = newRedoStack.pop(); // Remove the last item (redo operation)
      setRedoStack(newRedoStack);

      setUndoStack((prevUndoStack) => [...prevUndoStack, deepCopy(design)]); // Add current design to undoStack
      setDesign(nextDesign); // Set the design to the next design
    }
  }, [redoStack, design]); // this function handles the redoChange

  useUndoRedoShortcuts(undoChange, redoChange); // Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z and Ctrl/Cmd+Y

  useEscapeUnselect(design, updateDesign); // Escape unselects the keycaps, unless a modal is open

  useEffect(() => {
    if (isSaveSuccessful !== null) {
      setIsSaveSuccessful(null);
    }
  }, [design.keycapData]); // reseting the 'save' button, whenever the keycapData changes

  useEffect(() => {
    if (isCanvasTextMeasurementBlocked()) {
      setShowAlert(true);
    }
  }, []); // Run once on mount: detect if the browser’s tracking-protection is blocking canvas text-measurement. If it is, show the warning alert.

  const handleSelect = (key) => {
    updateDesign({ 'settings.selectedEditor': key });
  }; // this function runs whenever the user opens a differnt tab (layout, colors, text or icons)

  const handleSelectProfileChange = (event) => {
    const newProfile = event.target.value;
    updateDesign({ 'profileEditor.profile': newProfile });
  }; // sets the newly selected profile, when the user changes the profile in the customizer

  function handleKeycapClick(rowIndex, keycapIndex) {
    return (event) => {
      const updates = keycapClickUpdates(
        designRef.current, // always fresh (needed because in some cases the handleKeycapClick function accesses the old design state, e.g. in the driver.js tour created in the useEffect above)
        { rowIndex, keycapIndex },
        event
      ); // the new selection with every editor's entry, tag and colour selection reset, or null in an editor that doesn't select keycaps

      if (updates) {
        updateDesign(updates);
      }
    };
  } // this function is called whenever the user clicks on a keycap and depending on the editing mode the user is currently in, the click will be handled accordingly

  const keyboardReset = () => {
    resetDesignStorage(); // Reset the design state and the design_state in the user's localStorage
  }; // this function resets the entire design state (a first update put every keycap back to the defaults before the storage reset, which replaced the rows in the same tick, so it only doubled the undo snapshot: deleted in PR 31, user decision)

  const validateAdmin = async () => {
    try {
      await api.get('/api/general/validate-admin');
      // If no error is thrown, the user is authorized
    } catch (error) {
      toast.error('You are not authorized to download this SVG.');
      throw error;
    }
  }; // this function checks if the user is an admin in the backend (only checking userInfo on the client side is not secure)

  const downloadSvg = async () => {
    if (hasUnavailableUserUploadedIcons) {
      toast.info(t('customizer-screen-uploaded-icons-export-login-required')); // tells the user to sign in before exporting a design with uploaded icons
      return;
    }

    const canAccessUploadedIcons =
      await validateCurrentUserCanAccessDesignUploadedIcons({
        designToCheck: design,
        unavailableMessageKey:
          'customizer-screen-uploaded-icons-export-wrong-account',
      }); // checks that the signed-in user can access all uploaded icons before exporting the design

    if (!canAccessUploadedIcons) {
      return; // stop before generating an SVG export with missing uploaded icons
    }

    try {
      await validateAdmin(); // validate admin privileges

      const svgElement = document.getElementById('customDesignedKeycapSet');

      const clone = cloneAndPrepSvg(svgElement, layoutWidth, layoutHeight); // clones an SVG and sets dimensions, removing unwanted elements

      convertTextNodesToPaths(clone, fonts); // convert text ➜ paths (preserve fill/stroke)

      await inlinePngUserIconDataUrlsForSvgExport(clone); // replace uploaded PNG signed URLs with embedded data URLs for a self-contained SVG export

      normalizeAlphaPaintsForSvgExport(clone); // normalize rgba and #RRGGBBAA so exported/rendered SVG keeps alpha correctly

      const svgStr = svgToString(clone); // serializes SVG to string
      const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' }); // create a Blob from the SVG string with appropriate MIME type
      const url = URL.createObjectURL(blob); // generate a URL for the Blob

      const a = document.createElement('a');
      a.href = url;
      a.download = 'custom-keycaps-design.svg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      URL.revokeObjectURL(url); // clean up the URL object
    } catch (err) {
      console.error('Error during SVG download:', err);

      const errorMessage = getError(err);
      const errorDetails = err?.response?.data?.details;

      toast.error(
        errorMessage
          ? t(errorMessage, { details: errorDetails })
          : t('customizer-screen-svg-download-failed')
      );
    }
  }; // this function downloads the custom designed keycap set as a svg file onto the users computer and converts texts to paths

  const downloadPng = async (
    triggerDownload = false,
    desiredDPI,
    { skipUploadedIconAccessValidation = false } = {}
  ) => {
    if (hasUnavailableUserUploadedIcons) {
      toast.info(t('customizer-screen-uploaded-icons-export-login-required')); // tells the user to sign in before exporting a design with uploaded icons
      return null;
    }

    if (!skipUploadedIconAccessValidation) {
      const canAccessUploadedIcons =
        await validateCurrentUserCanAccessDesignUploadedIcons({
          designToCheck: design,
          unavailableMessageKey:
            'customizer-screen-uploaded-icons-export-wrong-account',
        }); // checks that the signed-in user can access all uploaded icons before exporting the design

      if (!canAccessUploadedIcons) {
        return null; // stop before generating a PNG export with missing uploaded icons
      }
    }

    try {
      const svgElement = document.getElementById('customDesignedKeycapSet');

      const clone = cloneAndPrepSvg(svgElement, layoutWidth, layoutHeight); // clones an SVG and sets dimensions, removing unwanted elements

      convertTextNodesToPaths(clone, fonts); // convert text ➜ paths (preserve fill/stroke)

      await inlinePngUserIconDataUrlsForSvgExport(clone); // replace uploaded PNG signed URLs with embedded data URLs before rendering the SVG to canvas

      normalizeAlphaPaintsForSvgExport(clone); // normalize rgba and #RRGGBBAA so exported/rendered SVG keeps alpha correctly

      const svgStr = svgToString(clone); // serializes SVG to string
      const blob = new Blob([svgStr], {
        type: 'image/svg+xml;charset=utf-8',
      }); // create a Blob from the SVG string with appropriate MIME type
      const url = URL.createObjectURL(blob); // generate a URL for the Blob

      const layoutWidthIn = layoutWidth / 25.4; // calculate the layout with in inches (layoutWidth is in millimeters (1 unit = 1 mm))
      const layoutHeightIn = layoutHeight / 25.4; // calculate the layout height in inches (layoutHeight is in millimeters (1 unit = 1 mm))
      const DPI = desiredDPI || 300; // 300 is the same dpi used for the keycap shadow png files

      // calculate the required pixel dimensions:
      const pixelWidth = Math.round(layoutWidthIn * DPI);
      const pixelHeight = Math.round(layoutHeightIn * DPI);

      // create a canvas with the required dimensions
      const canvas = document.createElement('canvas');
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
      const ctx = canvas.getContext('2d');

      const dataUrl = await new Promise((resolve, reject) => {
        const img = new Image();

        img.onload = () => {
          // draw the SVG onto the canvas
          try {
            ctx.drawImage(img, 0, 0, pixelWidth, pixelHeight); // draw the SVG onto the canvas
            URL.revokeObjectURL(url); // clean up the SVG Blob URL

            resolve(canvas.toDataURL('image/png')); // resolve with the PNG data URL
          } catch (error) {
            URL.revokeObjectURL(url);
            reject(error);
          }
        };

        img.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(error);
        }; // reject if the SVG image could not be loaded

        img.src = url; // set the image source to the Blob URL, triggering the load
      });

      if (triggerDownload) {
        const a = document.createElement('a');
        a.href = dataUrl;
        a.download = 'custom-keycaps-design.png';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }

      return dataUrl;
    } catch (err) {
      console.error('Error during PNG download:', err);

      const errorMessage = getError(err);
      const errorDetails = err?.response?.data?.details;

      toast.error(
        errorMessage
          ? t(errorMessage, { details: errorDetails })
          : t('customizer-screen-png-download-failed')
      );

      throw err;
    }
  }; // this function creates a .png file out of the custom designed keycap set. If the parameter is set to true, it also will be downloaded onto the users computer

  const saveCustomKeycapDesign = async (customName) => {
    try {
      dispatch({ type: 'CREATE_REQUEST' });
      setIsSaving(true);

      // Generate PNG data URL for the thumbnail
      const pngDataUrl = await downloadPng(false, 150); // generating PNG data URL (150 dpi)
      const response = await fetch(pngDataUrl);
      const thumbnailBlob = await response.blob();

      const migratedDesign = migrateDesignIfNeeded(design); // ensure the design uses the latest customizer schema before saving to the database

      const designToUpload = {
        ...migratedDesign, // use the migrated design state
        name: customName.trim(), // Add user-entered name here
        keycapData: prepareKeycapDataForUpload(migratedDesign.keycapData), // Replace keycapData with the processed array
      };

      const formData = new FormData();
      formData.append('thumbnail', thumbnailBlob, 'thumbnail.png');
      formData.append('design', JSON.stringify(designToUpload)); // Pass the modified design state as JSON (using JSON.stringify() is necessary because FormData does not natively support appending complex objects like plain objects or arrays)

      const { data } = await api.post('/api/designs', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      dispatch({ type: 'CREATE_SUCCESS' });
      toast.success(data.message);
      setDesignName('');
      setIsSaving(false);
      setIsSaveSuccessful(true);
    } catch (err) {
      const errorMessage = getError(err);
      toast.error(
        errorMessage
          ? t(errorMessage)
          : t('customizer-screen-something-went-wrong')
      );
      dispatch({
        type: 'CREATE_FAIL',
        payload: err.message,
      });
      setIsSaving(false); // Stop loading and show red X
      setIsSaveSuccessful(false);
    }
  }; // saves a keycap set design to the data base

  const handleSaveButtonClick = () => {
    // If user is not logged in, redirect to signup
    if (!userInfo) {
      navigate('/signup?redirect=/customizer');
      return;
    }
    // If logged in, open the modal
    setShowNameModal(true);
  };

  const handleConfirmName = async () => {
    // 1) Validate
    const trimmed = designNameToSave(designName); // the trimmed name, or null when it is shorter than two characters
    if (trimmed === null) {
      toast.error(t('customizer-screen-enter-valid-name'));
      return;
    }

    // 2) Close modal
    setShowNameModal(false);

    // 3) Proceed with the saving
    await saveCustomKeycapDesign(trimmed);
  };

  const startDragging = (event) => {
    const svg = event.target.ownerSVGElement; // accessing the SVG element (the entire keycap set) that contains the targets (the image)
    const pt = svg.createSVGPoint(); // creating an SVGPoint object for getting mouse position in SVG coordinate system

    const getMousePosition = (evt) => {
      pt.x = evt.clientX; // sets the pt‘s x property to the mouse’s screen coordinates
      pt.y = evt.clientY; // sets the pt‘s y property to the mouse’s screen coordinates
      const cursorpt = pt.matrixTransform(svg.getScreenCTM().inverse()); // transforming the point from screen coordinates to SVG coordinates
      return { x: cursorpt.x, y: cursorpt.y };
    }; // function to get the current mouse position in SVG coordinates

    const initialMousePos = getMousePosition(event); // gets the initial mouse position when dragging starts
    const initialImagePos = { ...design.imageEditor.position }; // gets the initial image position

    const onMouseMove = (moveEvent) => {
      const currentMousePos = getMousePosition(moveEvent); // gets the current mouse position
      const dx = currentMousePos.x - initialMousePos.x; // calculates the x change in mouse position
      const dy = currentMousePos.y - initialMousePos.y; // calculates the y change in mouse position

      // Rounding to the nearest 0.01
      const roundedX = parseFloat((initialImagePos.x + dx).toFixed(2));
      const roundedY = parseFloat((initialImagePos.y + dy).toFixed(2));

      updateDesign({
        'imageEditor.position': {
          x: roundedX,
          y: roundedY,
        },
      }); // updating the image position based on the change in mouse position
    }; // function to handle mouse move event during dragging

    const onMouseUp = () => {
      window.removeEventListener('mousemove', onMouseMove); // removing the mousemove event listener
      window.removeEventListener('mouseup', onMouseUp); // removing the mouseup event listener
    }; // function to handle mouse up event when dragging stops

    window.addEventListener('mousemove', onMouseMove); // adding new event listeners for mousemove
    window.addEventListener('mouseup', onMouseUp); // adding new event listeners for mouseup
  }; // this function handles dragging an image element across a svg element

  const handleWheel = (e) => {
    e.target.blur(); // Unfocus the input to prevent scrolling from changing the value
    e.preventDefault(); // Prevent the default scroll behavior
    setTimeout(() => e.target.focus(), 0); // Refocus it after the wheel event
  };

  const addToCartHandler = async () => {
    if (hasUnavailableUserUploadedIcons) {
      toast.info(
        t('customizer-screen-uploaded-icons-add-to-cart-login-required')
      ); // tells the user to sign in before adding a design with uploaded icons to the cart
      return;
    }

    try {
      const preparedDesign = migrateDesignIfNeeded(design); // ensure the design saved to IndexedDB uses the latest customizerVersion

      const canAccessUploadedIcons =
        await validateCurrentUserCanAccessDesignUploadedIcons({
          designToCheck: preparedDesign,
          unavailableMessageKey:
            'customizer-screen-uploaded-icons-add-to-cart-wrong-account',
        }); // checks that the signed-in user can access all uploaded icons before exporting the design

      if (!canAccessUploadedIcons) {
        return; // stop before generating the PNG or saving a broken cart item
      }

      const pngDataUrl = await downloadPng(false, 150, {
        skipUploadedIconAccessValidation: true,
      }); // Generate PNG data URL with 150 DPI
      if (!pngDataUrl) {
        return;
      }

      const uniqueCartId = uuidv4(); // Generate a unique ID for the cart item

      // Save large data (design and pngDataUrl) in IndexedDB
      await saveToIndexedDB({
        uniqueCartId, // Identifier to retrieve this data
        design: preparedDesign, // Design state
        pngDataUrl, // PNG data URL
      });

      // Save cart metadata in the localStorage
      const newCartItem = customizerCartItem(
        selectedProduct,
        preparedDesign,
        quantity,
        uniqueCartId
      ); // the product's fields with the sets, the profile and the IndexedDB id

      ctxDispatch({
        type: 'CART_ADD_ITEM',
        payload: newCartItem, // Send metadata only
      });
      toast.success(t('customizer-screen-added-to-cart'));

      navigate('/cart'); // Redirect to the CartScreen
    } catch (error) {
      toast.error(error);
    }
  };

  const handleDownloadReport = () => {
    // 1) If no data, exit
    if (!replacedFontsSummary) return;

    const { content, fileName } = designReplacedFontsReport(
      replacedFontsSummary,
      t
    ); // the report's text and file name

    // 2) Convert to a Blob & trigger download
    const blob = new Blob([content], {
      type: 'text/plain;charset=utf-8',
    });
    saveAs(blob, fileName);

    // 3) Mark as downloaded so user can close the modal without warning
    setHasDownloaded(true);
  }; // this function will trigger the download of the "Replaced Fonts Report" as a .txt file

  const handleCloseModal = () => {
    if (!hasDownloaded) {
      setShowConfirmClose(true); // Show confirmation modal
    } else {
      setShowReplacementsModal(false);
    }
  }; // this function makes sure that the user really wants to close the "Replaced Fonts Report" modal before downloading the report as a .txt file

  const handleLayoutSearchChange = (e) => {
    setLayoutSearchTerm(e.target.value);
    setShowLayoutList(true);
  }; // Handle user typing inside search bar of layoutEditor

  const handleSelectLayout = (layoutData) => {
    const selectedId = layoutData._id;

    // 1. Set the selected Layout, layoutSearchTerm and hide layout dropdown:
    setLayout(layoutData);
    setLayoutSearchTerm(getLayoutDisplayName(layoutData));
    setShowLayoutList(false); // Hide dropdown

    // 2. Set the selectedProduct, based on the new layout id:
    const selectedProduct = productOfLayout(products, selectedId);
    setSelectedProduct(selectedProduct);

    // 3. Set the relevantGroups state for the newly selected layout:
    setRelevantGroups(layoutGroups(layoutData.layout));

    // 4. Update the default keycapData array for the selected layout:
    const defaultKeycapData = blankKeycapData(layoutData.layout); // the layout's keycaps with the defaultKeycapValues, white and without entries
    setDefaultKeycapData(defaultKeycapData);

    // 5. Remove the predefined legends document:
    setSelectedLegend(null);

    // 6. Reset the totalVerticalSpacing value:
    totalVerticalSpacing = 0;

    // 7. Update the layoutWidth and layoutHeight for the SVG:
    setLayoutWidth(calculateLayoutWidth(layoutData.layout));
    setLayoutHeight(calculateLayoutHeight(layoutData.layout));

    // 8. Trigger a rerender of the keycap shadows:
    setTriggerKeycapShadowRerender((prev) => !prev);

    // 9. Update the design state:
    updateDesign(() => designForLayout(layoutData, defaultKeycapData)); // a new design on the layout: the default design state with the layout's name and blank keycaps, its first profile in stock and its custom-layout flag
  }; // sets states and triggers a re-render of the keycap shadows, when the user changes the layout in the customizer

  const filteredLayouts = searchedLayouts(layouts, layoutSearchTerm); // Filtered layouts based on the layoutName, alternativeNames and keyboardModels (utils/customizerLayout.js since chain 10, Oct 2026)

  function isCanvasTextMeasurementBlocked() {
    try {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      ctx.font = '16px sans-serif';

      // Two strings that *must* differ in width when measurement works
      const w1 = ctx.measureText('a').width;
      const w2 = ctx.measureText('ab').width;

      return !(w1 > 0 && w2 > w1); // Blocked when either width is 0 *or* both widths are identical
    } catch {
      return true; // Any thrown error also means the API is blocked
    }
  } // Detects whether the browser’s privacy-/fingerprint-protection is stopping canvas text-measurement (returns true if blocked).

  const isUserIconUsedInIndexedDBCartDesigns = async (assetId) => {
    for (const cartItem of cartItems) {
      if (!cartItem.uniqueCartId) continue;

      const asset = await getFromIndexedDB(cartItem.uniqueCartId); // fetch the cart asset from IndexedDB

      if (asset?.design && designUsesUserIconAsset(asset.design, assetId)) {
        return true;
      }
    }

    return false;
  }; // checks whether the uploaded icon is still used by any cart design

  const removeDeletedUserIconFromCurrentDesign = (deletedAssetId) => {
    setDesign((prevDesign) => {
      const { design: cleanedDesign, changed } = removeUserIconAssetFromDesign(
        prevDesign,
        deletedAssetId
      ); // remove the deleted uploaded icon from the current design if it is used there

      if (!changed) {
        return prevDesign;
      }

      safeLocalStorageSet(`${PREFIX}state`, JSON.stringify(cleanedDesign)); // keep the persisted current design in sync with the cleaned React state

      return cleanedDesign;
    });
  }; // removes the deleted uploaded icon from the current design state and localStorage

  const removeDeletedUserIconFromHistoryStacks = (deletedAssetId) => {
    const cleanStack = (stack) =>
      stack.map((historyDesign) => {
        const { design: cleanedDesign } = removeUserIconAssetFromDesign(
          historyDesign,
          deletedAssetId
        ); // remove the deleted uploaded icon from each saved history snapshot if it is used there

        return cleanedDesign;
      });

    setUndoStack((prevUndoStack) => cleanStack(prevUndoStack));
    setRedoStack((prevRedoStack) => cleanStack(prevRedoStack));
  }; // prevents undo/redo from restoring icon entries that reference a deleted uploaded icon

  const usedUserIconAssetIds = useMemo(
    () =>
      Array.isArray(design.keycapData) ? collectUserIconAssetIds(design) : [],
    [design]
  ); // collects uploaded icon asset ids currently used in the design; the one guard for a stored state without its rows, which the migration lets through and the collector no longer checks (user decision, Oct 2026)

  const hasUnavailableUserUploadedIcons = useMemo(
    () => uploadedIconsNeedSignIn(usedUserIconAssetIds, userInfo),
    [userInfo, usedUserIconAssetIds]
  ); // checks whether uploaded icons are unavailable because the user is signed out: the signed-out notice below, and the exports and the cart item, which need the uploaded icon assets, are blocked by it (utils/userIconAccess.js; the screen spelled the gate out again, and under a second name, until chain 10, Oct 2026)

  const { userIconAssetsById, setUserIconAssetsById, refreshUserIconAsset } =
    useUserIconAssets({
      usedUserIconAssetIds,
      signedIn: userInfo,
      t,
    }); // the render assets of the uploaded icons the design shows, loaded as the design uses them and refreshed before their links expire

  const {
    userIconPreviews,
    setUserIconPreviews,
    userIconPreviewsById,
    loadingUserIcons,
    fetchUserIconPreviews,
    refreshAllRelevantUserIconPreviewsOnce,
  } = useUserIconPreviews({ userInfo, usedUserIconAssetIds, t }); // the icon library's previews: the signed-in account's own icons and the ones the design shows, refreshed before their links expire

  const uploadedIconsUnavailableToastShownRef = useRef(false); // tracks whether the logged-out uploaded-icons warning has already been shown, so the user does not get repeated toast messages

  useEffect(() => {
    if (!hasUnavailableUserUploadedIcons) {
      return; // do nothing if there are no unavailable uploaded icons
    }

    if (uploadedIconsUnavailableToastShownRef.current) {
      return; // only show this warning once per page/session
    }

    uploadedIconsUnavailableToastShownRef.current = true; // remember that the warning has already been shown

    toast.info(t('customizer-screen-uploaded-icons-unavailable-logged-out')); // inform the user that uploaded icons will be restored after signing in again
  }, [hasUnavailableUserUploadedIcons, t]); // warns signed-out users when uploaded icons in the design are temporarily unavailable

  const validateCurrentUserCanAccessDesignUploadedIcons = ({
    designToCheck,
    unavailableMessageKey,
  }) =>
    verifyUserIconAssetAccess({
      assetIds: collectUserIconAssetIds(designToCheck),
      userInfo,
      t,
      loginRequiredKey: 'customizer-screen-uploaded-icons-login-required',
      unavailableKey: unavailableMessageKey,
    }); // verifies that the signed-in user can access all uploaded icons referenced by the design

  const designLayout = layoutOfDesign(layouts, design); // the layout the design sits on, as the list has it (the layout state follows it through the effect above, one render later): the layout search, the profile select, the add-to-cart button and the quantity field read it
  const designProfileStock = profileStock(
    designLayout,
    design.profileEditor.profile
  ); // the layout's stock in the design's profile: the quantity field's limit
  const designProfileSoldOut = isProfileSoldOut(
    designLayout,
    design.profileEditor.profile
  ); // the add-to-cart button's and the profile select's gate

  return (
    <Container fluid="xxl">
      <title>{t('customizer-screen-title')}</title>

      <h1 className="screen-heading">{t('customizer-screen-heading')}</h1>

      {loading ? (
        <div className="overlay">
          <LoadingLogo />
        </div>
      ) : error ? (
        <MessageBox className="danger-alert">{error}</MessageBox>
      ) : (
        <div>
          {isSaving ? (
            // display a loading symbol as long as the saving process of the design is taking:
            <div className="overlay">
              <LoadingLogo />
            </div>
          ) : null}

          {/* Replaced Fonts Report Modal */}
          <Modal
            show={showReplacementsModal}
            centered
            className="replaced-fonts-modal"
          >
            <Modal.Header>
              {/* We'll check if there's at least one deleted entry to change the message */}
              {(() => {
                const foundDeleted = replacedFonts
                  ? replacedFonts.deletedCount > 0
                  : false; // null until the load replaced fonts (see the state)

                return (
                  <div>
                    <Modal.Title>
                      {t('customizer-screen-modal-replaced-fonts-title')}
                    </Modal.Title>
                    <p>{t('customizer-screen-modal-replaced-fonts-3')}</p>
                    <p>{t('customizer-screen-modal-replaced-fonts-2')}</p>
                    {foundDeleted && (
                      <p className="modal-deleted-text-entries">
                        {t('customizer-screen-modal-replaced-fonts-1')}
                      </p>
                    )}
                  </div>
                );
              })()}
            </Modal.Header>

            <Modal.Body>
              {/* Check if replacedFontsSummary exists */}
              {replacedFontsSummary && (
                <div>
                  {/* Collect all replaced fonts for this design: */}
                  {(() => {
                    const { replacedFonts: replacedFontsArray, deletedCount } =
                      replacedFonts;

                    return (
                      <div>
                        {/* Summarized info: */}
                        <p className="modal-inactive-fonts">
                          {t('customizer-screen-modal-inactive-fonts')}{' '}
                          <strong>{replacedFontsArray.join(', ')}</strong>
                        </p>

                        {/* Show how many text entries were deleted */}
                        {deletedCount > 0 && (
                          <p className="modal-number-of-deleted-text-entries">
                            {t(
                              'customizer-screen-modal-number-of-removed-texts'
                            )}{' '}
                            <strong>{deletedCount}</strong>
                          </p>
                        )}

                        {/* Detailed info: */}
                        <div className="modal-replaced-fonts-summary-container">
                          {replacedFontsSummary.replacedKeycaps.map(
                            (keycap, index) => (
                              <div
                                key={`${replacedFontsSummary.designId}-${keycap.rowIndex}-${keycap.colIndex}-${index}`}
                                className="modal-replaced-fonts-per-keycap-container"
                              >
                                <strong>
                                  {index + 1}.{' '}
                                  {t('customizer-screen-modal-keycap-row')}{' '}
                                  {keycap.rowIndex},{' '}
                                  {t('customizer-screen-modal-column')}{' '}
                                  {keycap.colIndex}:
                                </strong>

                                <ul>
                                  {keycap.replacedTexts.map(
                                    (textItem, tIdx) => {
                                      // If wasDeleted => show "deleted" message
                                      if (textItem.wasDeleted) {
                                        return (
                                          <li
                                            key={tIdx}
                                            className="modal-replaced-text-entry"
                                          >
                                            {t(
                                              'customizer-screen-modal-report-content-1'
                                            )}{' '}
                                            "<em>{textItem.textValue}</em>"{' '}
                                            {t(
                                              'customizer-screen-modal-report-content-2'
                                            )}{' '}
                                            "
                                            <em>
                                              {textItem.originalFontFamily}
                                            </em>
                                            ")
                                          </li>
                                        );
                                      } else {
                                        // Normal replaced scenario
                                        return (
                                          <li key={tIdx}>
                                            {t(
                                              'customizer-screen-modal-report-content-3'
                                            )}{' '}
                                            "
                                            <em>
                                              {textItem.originalFontFamily}
                                            </em>
                                            "{' '}
                                            {t(
                                              'customizer-screen-modal-report-content-4'
                                            )}{' '}
                                            "<em>{textItem.textValue}</em>"
                                          </li>
                                        );
                                      }
                                    }
                                  )}
                                </ul>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                {t('customizer-screen-modal-close')}
              </Button>
              <Button variant="primary" onClick={handleDownloadReport}>
                {t('customizer-screen-modal-download-report')}
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Confirmation Modal for Closing Without Downloading */}
          <Modal
            show={showConfirmClose}
            centered
            className="close-confirmation-modal"
          >
            <Modal.Header>
              <Modal.Title>
                {t('customizer-screen-modal-close-without-download-title')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              {t('customizer-screen-modal-close-without-download-body')}
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => {
                  setShowConfirmClose(false);
                  setShowReplacementsModal(false);
                }}
              >
                {t('customizer-screen-modal-close-anyway')}
              </Button>
              <Button
                variant="primary"
                onClick={() => setShowConfirmClose(false)}
              >
                {t('customizer-screen-modal-go-back')}
              </Button>
            </Modal.Footer>
          </Modal>

          <Modal
            className="design-name-modal"
            show={showNameModal}
            onHide={() => setShowNameModal(false)}
          >
            <Modal.Header closeButton>
              <Modal.Title>
                {t('customizer-screen-modal-name-your-design')}
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <Form.Group controlId="designName">
                <Form.Control
                  type="text"
                  className="customizer-mobile-device-compatible-center"
                  value={designName}
                  onChange={(e) => setDesignName(e.target.value)}
                />
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button
                variant="secondary"
                onClick={() => setShowNameModal(false)}
              >
                {t('customizer-screen-modal-cancel')}
              </Button>

              <Button variant="primary" onClick={handleConfirmName}>
                {t('customizer-screen-modal-confirm')}
              </Button>
            </Modal.Footer>
          </Modal>

          <div className="customizer-settings-container">
            <SaveButton
              isSaveSuccessful={isSaveSuccessful}
              isSaving={isSaving}
              handleSavingDesign={handleSaveButtonClick}
            />

            <DropdownMenu
              userInfo={userInfo}
              undoChange={undoChange}
              redoChange={redoChange}
              keyboardReset={keyboardReset}
              downloadSvg={downloadSvg}
              downloadPng={downloadPng}
              tourRef={tourRef}
            />
          </div>

          {showCustomLayoutNotice && (
            <Alert variant="warning" className="custom-layout-notice">
              <div>
                <strong>{t('customizer-screen-custom-layout-notice-1')}</strong>{' '}
                {t('customizer-screen-custom-layout-notice-2')}
              </div>
              <Button
                variant="none"
                className="no-css"
                onClick={dismissRestrictedNotice}
                aria-label="Hide"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 16 16"
                >
                  <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                </svg>
              </Button>
            </Alert>
          )}

          <Nav
            justify
            variant="tabs"
            activeKey={design.settings.selectedEditor}
            onSelect={handleSelect}
            className="customizer-nav"
            id="customizer-tabs"
            ref={navRef}
          >
            <Nav.Item className="nav-layout" id="layout-tab">
              <Nav.Link eventKey="layout">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-grid-3x2-gap"
                  viewBox="0 0 16 16"
                >
                  <path d="M4 4v2H2V4zm1 7V9a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m0-5V4a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m5 5V9a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1m0-5V4a1 1 0 0 0-1-1H7a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1M9 4v2H7V4zm5 0h-2v2h2zM4 9v2H2V9zm5 0v2H7V9zm5 0v2h-2V9zm-3-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1zm1 4a1 1 0 0 0-1 1v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V9a1 1 0 0 0-1-1z" />
                </svg>

                {t('customizer-screen-layout')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="nav-profile" id="profile-tab">
              <Nav.Link eventKey="profile">
                <svg fill="#000000" viewBox="0 0 1024 1024" stroke="#000000">
                  <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                  <g
                    id="SVGRepo_tracerCarrier"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  ></g>
                  <g id="SVGRepo_iconCarrier">
                    <path d="M840 836H184c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h656c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8zm0-724H184c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h656c4.4 0 8-3.6 8-8v-60c0-4.4-3.6-8-8-8zM610.8 378c6 0 9.4-7 5.7-11.7L515.7 238.7a7.14 7.14 0 0 0-11.3 0L403.6 366.3a7.23 7.23 0 0 0 5.7 11.7H476v268h-62.8c-6 0-9.4 7-5.7 11.7l100.8 127.5c2.9 3.7 8.5 3.7 11.3 0l100.8-127.5c3.7-4.7.4-11.7-5.7-11.7H548V378h62.8z"></path>{' '}
                  </g>
                </svg>

                {t('customizer-screen-profile')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="nav-colors" id="colors-tab">
              <Nav.Link eventKey="colors">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-palette"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3m4 3a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M5.5 7a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0m.5 6a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3" />
                  <path d="M16 8c0 3.15-1.866 2.585-3.567 2.07C11.42 9.763 10.465 9.473 10 10c-.603.683-.475 1.819-.351 2.92C9.826 14.495 9.996 16 8 16a8 8 0 1 1 8-8m-8 7c.611 0 .654-.171.655-.176.078-.146.124-.464.07-1.119-.014-.168-.037-.37-.061-.591-.052-.464-.112-1.005-.118-1.462-.01-.707.083-1.61.704-2.314.369-.417.845-.578 1.272-.618.404-.038.812.026 1.16.104.343.077.702.186 1.025.284l.028.008c.346.105.658.199.953.266.653.148.904.083.991.024C14.717 9.38 15 9.161 15 8a7 7 0 1 0-7 7" />
                </svg>

                {t('customizer-screen-colors')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="nav-text" id="text-tab">
              <Nav.Link eventKey="text">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-fonts"
                  viewBox="1.5 1.5 13 13"
                >
                  <path d="M12.258 3h-8.51l-.083 2.46h.479c.26-1.544.758-1.783 2.693-1.845l.424-.013v7.827c0 .663-.144.82-1.3.923v.52h4.082v-.52c-1.162-.103-1.306-.26-1.306-.923V3.602l.431.013c1.934.062 2.434.301 2.693 1.846h.479z" />
                </svg>

                {t('customizer-screen-text')}
              </Nav.Link>
            </Nav.Item>

            <Nav.Item className="nav-icons" id="icons-tab">
              <Nav.Link eventKey="icons">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  className="bi bi-emoji-smile"
                  viewBox="0 0 16 16"
                >
                  <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                  <path d="M4.285 9.567a.5.5 0 0 1 .683.183A3.5 3.5 0 0 0 8 11.5a3.5 3.5 0 0 0 3.032-1.75.5.5 0 1 1 .866.5A4.5 4.5 0 0 1 8 12.5a4.5 4.5 0 0 1-3.898-2.25.5.5 0 0 1 .183-.683M7 6.5C7 7.328 6.552 8 6 8s-1-.672-1-1.5S5.448 5 6 5s1 .672 1 1.5m4 0c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5S9.448 5 10 5s1 .672 1 1.5" />
                </svg>

                {t('customizer-screen-icons')}
              </Nav.Link>
            </Nav.Item>

            {/* This Nav.Item is not yet responsive:
                <Nav.Item style={{ cursor: 'default' }}>
                  <Nav.Link
                    eventKey="image"
                    disabled
                    style={{ position: 'relative' }}
                  >
                    <div style={{ opacity: '0.5' }}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="bi bi-image"
                        viewBox="0 0 16 16"
                        style={{ marginRight: '10px', marginBottom: '4px' }}
                      >
                        <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0" />
                        <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1z" />
                      </svg>

                      {t('customizer-screen-image')}
                    </div>
                    <Badge className="coming-soon-badge">
                      {t('customizer-screen-coming-soon')}
                    </Badge>
                  </Nav.Link>
                </Nav.Item>
            */}
          </Nav>

          <div className="customizer-container">
            <div className="customizer-tools">
              {/* An alert telling the user to turn off the browsers tracking protection in the TextEditor and IconEditor: */}
              {showAlert &&
                (design.settings.selectedEditor === 'text' ||
                  design.settings.selectedEditor === 'icons') && (
                  <Alert
                    className="turn-off-tracking-alert"
                    variant="warning"
                    dismissible
                    onClose={() => setShowAlert(false)}
                  >
                    {t('customizer-screen-turn-off-tracking-alert')}
                  </Alert>
                )}

              {/* Layout Editor: */}
              {design.settings.selectedEditor === 'layout' && (
                <div ref={containerRef} className="customizer-layout-editor">
                  <Form.Group>
                    <Form.Control
                      type="text"
                      id="layout-select"
                      className={`customizer-mobile-device-compatible-top ${
                        isLayoutSoldOut(designLayout) ? 'sold-out-layout' : ''
                      }`}
                      value={layoutSearchTerm}
                      onChange={handleLayoutSearchChange}
                      onFocus={() => setShowLayoutList(true)}
                      placeholder={t(
                        'customizer-screen-search-keyboard-or-layout'
                      )}
                      required
                    />

                    <Dropdown
                      show={showLayoutList}
                      className="customizer-layout-dropdown"
                    >
                      <Dropdown.Menu>
                        {/* The "Search result" heading: */}
                        {filteredLayouts.length > 0 && (
                          <Dropdown.Header>
                            {t('customizer-screen-search-results')}
                          </Dropdown.Header>
                        )}

                        {/* Matching layouts based on the Search: */}
                        {filteredLayouts.map((layout) => {
                          const displayName = getLayoutDisplayName(layout);
                          const isSoldOut = isProfileSoldOut(
                            layout,
                            design.profileEditor.profile
                          ); // the stock the suffix of the display name shows (the three sections read the suffix's text back from the name until chain 8)

                          return (
                            <Dropdown.Item
                              key={layout._id}
                              className={`${
                                layout._id === design.layoutEditor.id
                                  ? 'selected-dropdown-item'
                                  : ''
                              } ${isSoldOut ? 'sold-out-layout' : ''}`}
                              onClick={() => handleSelectLayout(layout)}
                            >
                              {displayName}
                            </Dropdown.Item>
                          );
                        })}

                        {/* All restricted/custom layouts (layouts that are coming from products with visibility set to 'restricted'): */}
                        {layouts.some((l) => l.restricted) && (
                          <>
                            <Dropdown.Divider />

                            <Dropdown.Header>
                              {t('customizer-screen-my-layouts')}
                            </Dropdown.Header>

                            {layouts
                              .filter((l) => l.restricted)
                              .map((layout) => {
                                const displayName =
                                  getLayoutDisplayName(layout);
                                const isSoldOut = isProfileSoldOut(
                                  layout,
                                  design.profileEditor.profile
                                );

                                return (
                                  <Dropdown.Item
                                    key={layout._id}
                                    className={`${
                                      layout._id === design.layoutEditor.id
                                        ? 'selected-dropdown-item'
                                        : ''
                                    } ${isSoldOut ? 'sold-out-layout' : ''}`}
                                    onClick={() => handleSelectLayout(layout)}
                                  >
                                    {displayName}
                                  </Dropdown.Item>
                                );
                              })}
                          </>
                        )}

                        {/* All public layouts (layouts that are coming from products with visibility set to 'public'): */}
                        {(() => {
                          const hasRestricted = layouts.some(
                            (l) => l.restricted
                          );
                          const publicLayouts = layouts.filter(
                            (l) => !l.restricted
                          );

                          if (publicLayouts.length === 0) return null;

                          return (
                            <>
                              <Dropdown.Divider />

                              <Dropdown.Header>
                                {hasRestricted
                                  ? t('customizer-screen-public-layouts')
                                  : t('customizer-screen-all')}
                              </Dropdown.Header>

                              {publicLayouts.map((layout) => {
                                const displayName =
                                  getLayoutDisplayName(layout);
                                const isSoldOut = isProfileSoldOut(
                                  layout,
                                  design.profileEditor.profile
                                );

                                return (
                                  <Dropdown.Item
                                    key={layout._id}
                                    className={`${
                                      layout._id === design.layoutEditor.id
                                        ? 'selected-dropdown-item'
                                        : ''
                                    } ${isSoldOut ? 'sold-out-layout' : ''}`}
                                    onClick={() => handleSelectLayout(layout)}
                                  >
                                    {displayName}
                                  </Dropdown.Item>
                                );
                              })}
                            </>
                          );
                        })()}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Form.Group>
                </div>
              )}

              {/* Profile Editor: */}
              {design.settings.selectedEditor === 'profile' && (
                <div className="customizer-profile-editor">
                  <Form>
                    <Form.Group controlId="profileSelect">
                      <Form.Select
                        value={design.profileEditor.profile}
                        id="profile-select"
                        className={`customizer-mobile-device-compatible-top ${
                          designProfileSoldOut ? 'sold-out-profile' : ''
                        }`}
                        onChange={handleSelectProfileChange}
                      >
                        {layoutProfiles(designLayout).map((profile) => (
                          <option key={profile} value={profile}>
                            {profileOptionLabel(designLayout, profile, t)}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Form>
                </div>
              )}

              {/* Keycap Color Editor: */}
              <KeycapColorEditor
                isActive={design.settings.selectedEditor === 'colors'} // Do not render anything if not active. (this is better than conditionally rendering the KeycapColorEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
                design={design}
                hasAssignedColorGroups={hasAssignedColorGroups}
                relevantGroups={relevantGroups}
                updateDesign={updateDesign}
              />

              {/* Text Editor: */}
              <TextEditor
                ref={textEditorRef}
                isActive={design.settings.selectedEditor === 'text'} // Do not render anything if not active. (this is better than conditionally rendering the TextEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
                fonts={fonts}
                design={design}
                hasAssignedKeycapIDs={hasAssignedKeycapIDs}
                selectedLegend={selectedLegend}
                setSelectedLegend={setSelectedLegend}
                predefinedTagColors={predefinedTagColors}
                updateDesign={updateDesign}
              />

              {/* Icon Editor: */}
              <IconEditor
                userInfo={userInfo}
                ref={iconEditorRef}
                isActive={design.settings.selectedEditor === 'icons'} // Do not render anything if not active. (this is better than conditionally rendering the IconEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
                design={design}
                updateDesign={updateDesign}
                predefinedTagColors={predefinedTagColors}
                userIconPreviews={userIconPreviews}
                userIconPreviewsById={userIconPreviewsById}
                userIconAssetsById={userIconAssetsById}
                fetchUserIconPreviews={fetchUserIconPreviews}
                loadingUserIcons={loadingUserIcons}
                setUserIconPreviews={setUserIconPreviews}
                setUserIconAssetsById={setUserIconAssetsById}
                refreshAllRelevantUserIconPreviewsOnce={
                  refreshAllRelevantUserIconPreviewsOnce
                }
                removeDeletedUserIconFromCurrentDesign={
                  removeDeletedUserIconFromCurrentDesign
                }
                removeDeletedUserIconFromHistoryStacks={
                  removeDeletedUserIconFromHistoryStacks
                }
                isUserIconUsedInIndexedDBCartDesigns={
                  isUserIconUsedInIndexedDBCartDesigns
                }
              />

              {/* Image Editor: */}
              {/* The ImageEditor is an unfinished feature — only partly implemented, so it stays commented out until it's done:
                  <ImageEditor
                    isActive={design.settings.selectedEditor === 'image'} // Do not render anything if not active. (this is better than conditionally rendering the ImageEditor component in the CustomizerScreen, because now this component doesn't mound and unmount every time the user switches tabs. This cause the useEffect hooks to be triggered for every mount and the states to be set back to defaults whenever it is unmounted)
                    design={design}
                    layoutSoldOut={layoutSoldOut}
                    updateDesign={updateDesign}
                  />
              */}
            </div>

            {/* click-away to unselect, not a control of its own — keyboard users press Escape instead (see the effect above), so the interaction rules are deliberately off here: */}
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
            <div
              className="shared-content"
              onClick={(event) => {
                if (event.target === event.currentTarget) {
                  // If the user clicks on an empty space inside the div, unselect the keycap:
                  updateDesign({
                    'settings.selectedKeycaps': [],
                    'settings.isoEnterSelected': false,
                    'keycapColorEditor.selectedColorEntry': null,
                  });
                }
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg" // Ensure correct namespace
                viewBox={`0 0 ${layoutWidth} ${layoutHeight}`}
                style={{
                  width: '100%',
                  height: 'auto',
                  maxWidth: layoutWidth * 3,
                  overflow: 'visible', // To prevent clipping (is needed to ensure that the svg is not being clipped at the bottom and right side due to rendering nuances like rounding errors and anti-aliasing)
                }}
                version="1.1"
                id="customDesignedKeycapSet"
                onClick={(event) => {
                  if (event.target === event.currentTarget) {
                    // if the user clicks on an empty space inside the svg, unselect the keycap:
                    updateDesign({
                      'settings.selectedKeycaps': [],
                      'settings.isoEnterSelected': false,
                      'keycapColorEditor.selectedColorEntry': null,
                    });
                  }
                }}
              >
                {
                  <KeycapStroke
                    keycapData={design.keycapData}
                    totalVerticalSpacing={totalVerticalSpacing}
                    keycapColorEditor={design.keycapColorEditor}
                    settings={design.settings}
                    hoveredKeycap={hoveredKeycap}
                  />
                }

                {
                  <KeycapBaseLayer
                    keycapData={design.keycapData}
                    totalVerticalSpacing={totalVerticalSpacing}
                    dataComingFromLayoutEditor={false}
                    dataComingFromColorPicker={false}
                    visibleGroup={null}
                    hexColorCode={null}
                    dataComingFromLegendsEditor={false}
                  />
                }

                {
                  (totalVerticalSpacing = 0) /* Reset totalVerticalSpacing to 0, becaus it will be reused in the next keycapData.map function */
                }

                {
                  <KeycapImageRendering
                    design={design}
                    totalVerticalSpacing={totalVerticalSpacing}
                  />
                }

                {
                  <KeycapOverlay
                    keycapData={design.keycapData}
                    userIconAssetsById={userIconAssetsById}
                    refreshUserIconAsset={refreshUserIconAsset}
                    settings={design.settings}
                    selectedTextIndex={design.textEditor.selectedTextIndex}
                    selectedIconIndex={design.iconEditor.selectedIconIndex}
                    totalVerticalSpacing={totalVerticalSpacing}
                    handleKeycapClick={handleKeycapClick}
                    dataComingFromLayoutEditor={false}
                    dataComingFromLegendsEditor={false}
                    fonts={fonts}
                    setFonts={setFonts}
                    loadFont={loadFont}
                    setHoveredKeycap={setHoveredKeycap}
                  />
                }

                {
                  <StaticKeycapShadows
                    keycapData={design.keycapData}
                    triggerKeycapShadowRerender={triggerKeycapShadowRerender}
                    handleKeycapClick={handleKeycapClick}
                    dataComingFromLayoutEditor={false}
                    dataComingFromLegendsEditor={false}
                  />
                }

                {design.settings.selectedEditor === 'image' && (
                  <>
                    {/* These rect elemenst will porvide a way for the user to drag the image, whenever in 'image' keycapEditingMode. Using two react elements, to achieve the effect where both the dashes and the spaces between them are colored: */}
                    <rect
                      className="dont-include-in-download"
                      x={0}
                      y={0}
                      width={design.imageEditor.dimensions.width}
                      height={design.imageEditor.dimensions.height}
                      transform={`translate(${design.imageEditor.position.x}, ${
                        design.imageEditor.position.y
                      }) rotate(${design.imageEditor.rotation}, ${
                        design.imageEditor.dimensions.width / 2
                      }, ${design.imageEditor.dimensions.height / 2})`}
                      fill="transparent"
                      style={{ cursor: 'move' }}
                      onMouseDown={startDragging}
                      stroke="white"
                      strokeWidth="0.2"
                    />
                    <rect
                      className="dont-include-in-download"
                      x={0}
                      y={0}
                      width={design.imageEditor.dimensions.width}
                      height={design.imageEditor.dimensions.height}
                      transform={`translate(${design.imageEditor.position.x}, ${
                        design.imageEditor.position.y
                      }) rotate(${design.imageEditor.rotation}, ${
                        design.imageEditor.dimensions.width / 2
                      }, ${design.imageEditor.dimensions.height / 2})`}
                      fill="transparent"
                      style={{ cursor: 'move' }}
                      onMouseDown={startDragging}
                      stroke="black"
                      strokeWidth="0.2"
                      strokeDasharray="3"
                    />
                  </>
                )}
              </svg>
            </div>
          </div>

          <div className="customizer-product-info-container">
            <div className="customizer-quantity-container">
              <span>{t('customizer-screen-quantity')}</span>

              <div className="customizer-quantity-input-container">
                <Form.Control
                  className="customizer-mobile-device-compatible-center"
                  type="number"
                  min="1"
                  max={customizerQuantityLimit(designProfileStock)}
                  defaultValue={quantity}
                  onBlur={(e) => {
                    const safe = customizerQuantityFromInput(
                      e.target.value,
                      designProfileStock
                    ); // 1 to 100 and the stock, 1 for anything but a number (the field's own rule until chain 8 read parseInt and stopped at the stock alone)
                    setQuantity(safe);
                    e.target.value = safe; // reflect clamped value in the UI
                  }}
                  onWheel={handleWheel}
                />
              </div>
            </div>

            <div className="customizer-add-to-cart-container">
              <Button
                id="add-to-cart"
                onClick={addToCartHandler}
                variant="primary"
                disabled={designProfileSoldOut}
              >
                {designProfileSoldOut
                  ? t('customizer-screen-sold-out-button')
                  : t('customizer-screen-add-to-cart-button')}
              </Button>
            </div>

            <div className="customizer-price-container">
              <span className="customizer-price-label">
                {t('customizer-screen-price')}
              </span>

              <span className="customizer-price-value">
                {selectedProduct.unitGross
                  ? formatEuro(selectedProduct.unitGross)
                  : null}
              </span>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
}
